import { Box, Button, Flex, Input, Select, SimpleGrid, Stack, Text } from '@chakra-ui/react'
import { useState } from 'react'
import { RiAddLine, RiDownload2Line, RiFilterLine, RiUserSearchLine } from 'react-icons/ri'
import { ClientSelectionModal } from './ClientSelectionModal'
import { EmployeeSelectionModal } from './EmployeeSelectionModal'
import { ProviderSelectionModal } from './ProviderSelectionModal'

interface FiltersToolbarProps {
  filters: any
  onFiltersChange: (filters: any) => void
  onApply: () => void
  onClear: () => void
  onCreate: () => void
  onExport: (format: 'csv' | 'pdf') => void
  paymentMethodOptions: { label: string; value: number }[]
  clientOptions: { label: string; value: number }[]
  providerOptions: { label: string; value: number }[]
  employeeOptions: { label: string; value: number }[]
}

const statusOptions = [
  { label: 'Todas', value: 'TODOS' },
  { label: 'Pago', value: 'PAGO' },
  { label: 'Em Aberto', value: 'EM_ABERTO' },
]

const categoryOptions = [
  { label: 'Todas', value: 'TODOS' },
  { label: 'Empresa', value: 'EMPRESA' },
  { label: 'Pessoal', value: 'PESSOAL' },
  { label: 'Outros', value: 'OUTROS' },
]

const referenceOptions = [
  { label: 'Data de Vencimento', value: 'VENCIMENTO' },
  { label: 'Data de Movimento', value: 'MOVIMENTO' },
]

const selectStyles = {
  bg: 'gray.700',
  color: 'white',
  borderColor: 'gray.600',
  _hover: { borderColor: 'pink.400' },
  _focus: { borderColor: 'pink.400', color: 'white', boxShadow: 'none' },
  sx: { option: { color: 'black', backgroundColor: 'white' } },
} as const

export function FiltersToolbar({
  filters,
  onFiltersChange,
  onApply,
  onClear,
  onCreate,
  onExport,
  paymentMethodOptions,
  clientOptions,
  providerOptions,
  employeeOptions,
}: FiltersToolbarProps) {
  const [isClientModalOpen, setIsClientModalOpen] = useState(false)
  const [isProviderModalOpen, setIsProviderModalOpen] = useState(false)
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false)

  const handleChange = (field: string, value: any) => {
    onFiltersChange({ [field]: value })
  }

  const handleClientSelection = (selectedClients: number[]) => {
    handleChange('client', selectedClients)
  }

  const handleProviderSelection = (selectedProviders: number[]) => {
    handleChange('provider', selectedProviders)
  }

  const handleEmployeeSelection = (selectedEmployees: number[]) => {
    handleChange('employee', selectedEmployees)
  }

  const getSelectedClientsText = () => {
    const clientArray = Array.isArray(f.client) ? f.client : []
    if (clientArray.length === 0) return 'Selecionar clientes'
    if (clientArray.length === 1) {
      const client = clientOptions.find((c) => c.value === clientArray[0])
      return client ? client.label : '1 cliente selecionado'
    }
    return `${clientArray.length} clientes selecionados`
  }

  const getSelectedProvidersText = () => {
    const providerArray = Array.isArray(f.provider) ? f.provider : []
    if (providerArray.length === 0) return 'Selecionar fornecedores'
    if (providerArray.length === 1) {
      const provider = providerOptions.find((p) => p.value === providerArray[0])
      return provider ? provider.label : '1 fornecedor selecionado'
    }
    return `${providerArray.length} fornecedores selecionados`
  }

  const getSelectedEmployeesText = () => {
    const employeeArray = Array.isArray(f.employee) ? f.employee : []
    if (employeeArray.length === 0) return 'Selecionar funcionários'
    if (employeeArray.length === 1) {
      const employee = employeeOptions.find((e) => e.value === employeeArray[0])
      return employee ? employee.label : '1 funcionário selecionado'
    }
    return `${employeeArray.length} funcionários selecionados`
  }

  const toStr = (v: unknown) => (v === undefined || v === null ? '' : String(v))
  const f = filters as any
  const startDateStr = toStr(f.start_date)
  const endDateStr = toStr(f.end_date)
  const referenceTypeStr = toStr(f.reference_type)
  const statusStr = toStr(f.status ?? 'TODOS')
  const clientArray = Array.isArray(f.client) ? f.client : []
  const providerArray = Array.isArray(f.provider) ? f.provider : []
  const employeeArray = Array.isArray(f.employee) ? f.employee : []
  const documentStr = toStr(f.document_number ?? '')
  const categoryStr = toStr(f.category ?? 'TODOS')
  const paymentMethodStr = toStr(f.payment_method_id ?? '')
  const osStr = toStr(f.id_os ?? '')

  // Simplificar tipos dos inputs/selects para evitar unions complexos de TS
  const AnySelect: any = Select
  const AnyInput: any = Input

  return (
    <Box bg="gray.800" borderRadius="md" p={6} mb={6} {...({} as any)}>
      <Flex align="center" justify="space-between" mb={6} flexWrap="wrap" gap={4}>
        <Stack spacing={1}>
          <Flex align="center" gap={2} color="gray.300" {...({} as any)}>
            <RiFilterLine />
            <Text fontWeight="bold">Filtros</Text>
          </Flex>
          <Text fontSize="sm" color="gray.400">
            Ajuste os filtros para localizar lançamentos financeiros específicos
          </Text>
        </Stack>
        <Flex gap={2} flexWrap="wrap">
          <Button leftIcon={<RiDownload2Line />} variant="outline" onClick={() => onExport('csv')}>
            Exportar CSV
          </Button>
          <Button leftIcon={<RiAddLine />} colorScheme="pink" onClick={onCreate}>
            Novo lançamento
          </Button>
        </Flex>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 7 }} spacing={4} mb={4} {...({} as any)}>
        <Box>
          <Text fontSize="sm" color="gray.400" mb={2}>
            Data inicial
          </Text>
          <AnyInput
            type="date"
            value={startDateStr}
            onChange={(event) => handleChange('start_date', String(event.target.value))}
          />
        </Box>
        <Box>
          <Text fontSize="sm" color="gray.400" mb={2}>
            Data final
          </Text>
          <AnyInput
            type="date"
            value={endDateStr}
            onChange={(event) => handleChange('end_date', String(event.target.value))}
          />
        </Box>
        <Box>
          <Text fontSize="sm" color="gray.400" mb={2}>
            Período por
          </Text>
          <AnySelect
            value={referenceTypeStr}
            onChange={(event) => handleChange('reference_type', String(event.target.value))}
            {...selectStyles}>
            {referenceOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </AnySelect>
        </Box>
        <Box>
          <Text fontSize="sm" color="gray.400" mb={2}>
            Situação
          </Text>
          <AnySelect
            value={statusStr}
            onChange={(event) => handleChange('status', String(event.target.value))}
            {...selectStyles}>
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </AnySelect>
        </Box>
        <Box>
          <Text fontSize="sm" color="gray.400" mb={2}>
            Clientes
          </Text>
          <Button
            leftIcon={<RiUserSearchLine />}
            variant="outline"
            width="100%"
            justifyContent="flex-start"
            bg="gray.700"
            color="white"
            borderColor="gray.600"
            _hover={{ borderColor: 'pink.400' }}
            _focus={{ borderColor: 'pink.400', boxShadow: 'none' }}
            onClick={() => setIsClientModalOpen(true)}>
            {getSelectedClientsText()}
          </Button>
        </Box>
        <Box>
          <Text fontSize="sm" color="gray.400" mb={2}>
            Fornecedores
          </Text>
          <Button
            leftIcon={<RiUserSearchLine />}
            variant="outline"
            width="100%"
            justifyContent="flex-start"
            bg="gray.700"
            color="white"
            borderColor="gray.600"
            _hover={{ borderColor: 'pink.400' }}
            _focus={{ borderColor: 'pink.400', boxShadow: 'none' }}
            onClick={() => setIsProviderModalOpen(true)}>
            {getSelectedProvidersText()}
          </Button>
        </Box>
        <Box>
          <Text fontSize="sm" color="gray.400" mb={2}>
            Funcionários
          </Text>
          <Button
            leftIcon={<RiUserSearchLine />}
            variant="outline"
            width="100%"
            justifyContent="flex-start"
            bg="gray.700"
            color="white"
            borderColor="gray.600"
            _hover={{ borderColor: 'pink.400' }}
            _focus={{ borderColor: 'pink.400', boxShadow: 'none' }}
            onClick={() => setIsEmployeeModalOpen(true)}>
            {getSelectedEmployeesText()}
          </Button>
        </Box>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 7 }} spacing={4}>
      <Box>
          <Text fontSize="sm" color="gray.400" mb={2}>
            Documento
          </Text>
          <AnyInput
            placeholder="Número do documento"
            value={documentStr}
            onChange={(event) => handleChange('document_number', String(event.target.value))}
          />
        </Box>
        <Box>
          <Text fontSize="sm" color="gray.400" mb={2}>
            OS
          </Text>
          <AnyInput
            placeholder="Número da OS"
            value={osStr}
            onChange={(event) => handleChange('id_os', String(event.target.value))}
          />
        </Box>
        <Box>
          <Text fontSize="sm" color="gray.400" mb={2}>
            Tipo
          </Text>
          <AnySelect
            value={f.ui_nature ?? 'TODOS'}
            onChange={(event) => handleChange('ui_nature', String(event.target.value))}
            {...selectStyles}>
            <option value="TODOS">Todos</option>
            <option value="RECEITAS">Receitas</option>
            <option value="DESPESAS">Despesas</option>
          </AnySelect>
        </Box>
        <Box>
          <Text fontSize="sm" color="gray.400" mb={2}>
            Recorrência
          </Text>
          <AnySelect
            value={f.ui_recurrence ?? 'TODOS'}
            onChange={(event) => handleChange('ui_recurrence', String(event.target.value))}
            {...selectStyles}>
            <option value="TODOS">Todos</option>
            <option value="RECORRENTES">Recorrentes</option>
            <option value="AVULSOS">Avulsos</option>
          </AnySelect>
        </Box>
        <Box>
          <Text fontSize="sm" color="gray.400" mb={2}>
            Categoria
          </Text>
          <AnySelect
            value={categoryStr}
            onChange={(event) => handleChange('category', String(event.target.value))}
            {...selectStyles}>
            {categoryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </AnySelect>
        </Box>
        <Box>
          <Text fontSize="sm" color="gray.400" mb={2}>
            Forma de pagamento
          </Text>
          <AnySelect
            placeholder="Todas"
            value={paymentMethodStr}
            onChange={(event) => {
              const value = String(event.target.value)
              handleChange('payment_method_id', value ? Number(value) : '')
            }}
            {...selectStyles}>
            {paymentMethodOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </AnySelect>
        </Box>
        <Flex align="flex-end" gap={2} gridColumn={{ base: '1 / -1', lg: 'auto' }}>
          <Button colorScheme="blue" onClick={onApply}>
            Aplicar filtros
          </Button>
          <Button variant="ghost" onClick={onClear}>
            Limpar
          </Button>
        </Flex>
      </SimpleGrid>

      <ClientSelectionModal
        isOpen={isClientModalOpen}
        onClose={() => setIsClientModalOpen(false)}
        onConfirm={handleClientSelection}
        clientOptions={clientOptions}
        selectedClients={Array.isArray(f.client) ? f.client : []}
      />

      <ProviderSelectionModal
        isOpen={isProviderModalOpen}
        onClose={() => setIsProviderModalOpen(false)}
        onConfirm={handleProviderSelection}
        providerOptions={providerOptions}
        selectedProviders={Array.isArray(f.provider) ? f.provider : []}
      />

      <EmployeeSelectionModal
        isOpen={isEmployeeModalOpen}
        onClose={() => setIsEmployeeModalOpen(false)}
        onConfirm={handleEmployeeSelection}
        employeeOptions={employeeOptions}
        selectedEmployees={Array.isArray(f.employee) ? f.employee : []}
      />
    </Box>
  )
}
